import { SelectionMode as SelectionModeEnum } from "@fluentui/utilities";
import { IColumn } from "./column";
import { IFormField } from "./form";

export interface IPossibleValue {
  name?: string | undefined;
  value?: any | undefined;
}

export type FontSize =
  | "tiny"
  | "xSmall"
  | "small"
  | "smallPlus"
  | "medium"
  | "mediumPlus"
  | "large"
  | "xLarge"
  | "xxLarge"
  | "mega";

export type FontWeight =
  | "light"
  | "normal"
  | "medium"
  | "semiBold"
  | "bold"
  | "extraBold"
  | "ultraBold";

export type Align =
  | "start"
  | "center"
  | "space-between"
  | "space-around"
  | "space-evently"
  | "flex-end"
  | "baseline";

export type SelectionMode = "none" | "single" | "multiple";

export type BorderType = "thin" | "shadow";

export type ComparisonOperator =
  | "equals"
  | "notEquals"
  | "contains"
  | "noContains"
  | "present"
  | "notPresent"
  | "greaterThan"
  | "greaterThanOrEqual"
  | "lessThan"
  | "lessThanOrEqual"
  | "startsWith";

export type ScreenWidth = "lg" | "md" | "sm" | "xs" | "xxs";

export interface IDropdownOption {
  key: string;
  text: string;
  selected?: boolean | undefined;
  value: any;
}

export interface IScope {
  name?: string | undefined;
  path?: string | undefined;
}

export interface IScopeBase {
  path: string;
  body?: any;
}

export interface ISearchScope extends IScopeBase {
  name: string;
  columns: IColumn[];
}

export enum DataLoadMode {
  Auto = "auto",
  OnSearch = "onSearch",
}

export interface IObjectPickerModalOptions {
  title?: string | undefined;
  description: string;
  closeButtonName?: string;
  dataLoadMode: DataLoadMode;
  selectionMode?: SelectionModeEnum;
}

export interface IIcon {
  roundBorder?: boolean | undefined;
  width?: number | undefined;
  height?: number | undefined;
  type: string;
  id?: string | undefined;
  name?: string | undefined;
  fontSize?: FontSize | undefined;
  fontWeight?: FontWeight | undefined;
  bindings?: { [key: string]: string } | undefined;
  iconName?: string | undefined;
  rotate?: string | undefined;
  backward?: boolean | undefined;
  color?: string | undefined;
}

export interface IUIItem {
  type: string;
  id?: string | undefined;
  name?: string | undefined;
  fontSize?: FontSize | undefined;
  fontWeight?: FontWeight | undefined;
  bindings?: { [key: string]: string } | undefined;
  readOnly?: boolean;
  isStandalone?: boolean;
}

export interface ILayoutItem {
  stretch?: boolean | undefined;
  childrenGap?: number | undefined;
  justifyContent?: Align | undefined;
  valign?: boolean | undefined;
  border?: BorderType | undefined;
  type: string;
  id?: string | undefined;
  name?: string | undefined;
  fontSize?: FontSize | undefined;
  fontWeight?: FontWeight | undefined;
  bindings?: { [key: string]: string } | undefined;
  items?: string[] | undefined;
}

export type HttpMethods = "GET" | "POST" | "PATCH" | "PUT" | "DELETE" | "REDIRECT";

export interface IAjaxAction {
  url?: string | undefined;
  validationUrl?: string | undefined;
  method: HttpMethods;
  body?: string | undefined;
  bodyType?: string | undefined;
  sendEmptyValues?: boolean | undefined;
  isStream?: boolean | undefined;
  isBatch?: boolean | undefined;
  bodyProperty?: string | undefined;
  response?: { [key: string]: string } | undefined;
  confirmation?: IConfirmationParameters | undefined;
}

export interface IButton {
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  hideWhenEmpty?: boolean | undefined;
  readOnly?: boolean | undefined;
  label?: string | undefined;
  tooltip?: string | undefined;
  placeHolder?: string | undefined;
  defaultValue?: string | undefined;
  shortName?: string | undefined;
  autoFocus?: boolean | undefined;
  hideRequiredAsterix?: boolean | undefined;
  hideCopyButton?: boolean | undefined;
  valueDependsOn?: string | undefined;
  isConfidential?: boolean | undefined;
  isPicker?: boolean | undefined;
  order?: number | undefined;
  collapseIfDisabled?: boolean | undefined;
  visible?: boolean | undefined;
  errors?: IValidationErrors | undefined;
  dataType?: string | undefined;
  type: string;
  id?: string | undefined;
  name?: string | undefined;
  fontSize?: FontSize | undefined;
  fontWeight?: FontWeight | undefined;
  bindings?: { [key: string]: string } | undefined;
  text?: string | undefined;
  icon?: IIcon | undefined;
  image?: string | undefined;
  iconAtRight?: boolean | undefined;
  permittedRole?: string | undefined;
  action?: string | undefined;
  ajaxAction?: IAjaxAction | undefined;
  theme?: string | undefined;
  autoRun?: boolean | undefined;
}

export interface IConfirmationParameters {
  title?: string | undefined;
  description?: string | undefined;
  okText?: string | undefined;
  cancelText?: string | undefined;
  actionItemDisplayDescription?: IActionItemDisplayDescription | undefined;
}

export interface IActionItemDisplayDescription {
  format?: string | undefined;
  multipleOnly: boolean;
}

export interface IValidationErrors {
  required?: string | undefined;
  pattern?: string | undefined;
}

export interface IProxyAddressesGrid extends IFormField {
  columns: IColumn[];
  value?: any[] | undefined;
  noItemsText?: string;
  idPropName?: string;
  domains?: IPossibleValue[] | undefined;
  allowCustomDomainInput?: boolean;
  defaultDomain?: string;
}

export type ActionParameters = {
  objectIdPropName?: string;
  objectSidPropName?: string;
  objectNamePropName?: string;
  additionalParameters?: Record<string, unknown>;
};

export interface IGlobalSearchDescriptor {
  title: string;
  globalScopes: IDropdownOption[];
  tip?: string;
  globalScopeTitle?: string;
  lookupScope: IScopeBase;
}

export interface IScheme {
  stretch?: boolean | undefined;
  childrenGap?: number | undefined;
  justifyContent?: Align | undefined;
  verticalAlign?: Align;
  valign?: boolean | undefined;
  border?: BorderType | undefined;
  type: string;
  id?: string | undefined;
  name?: string | undefined;
  fontSize?: FontSize | undefined;
  fontWeight?: FontWeight | undefined;
  bindings?: { [key: string]: string } | undefined;
  items?: IUIItem[] | undefined;
  layout?: ILayoutItem[] | undefined;
  userRoles?: string[] | undefined;
  version?: number | undefined;
  title?: string;
  hidden?: boolean;
}

export type OverlayIcon = IIcon & { position?: OverlayIconPosition | undefined };

export enum OverlayIconPosition {
  BottomRightIcon,
  RightOfText,
}
